import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Crown, Gift, Trophy } from "lucide-react";
import { getReferralLeaderboard } from "@/lib/growth.functions";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { Reveal, ScrollProgress } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/referral")({
  head: () => ({
    meta: [
      { title: "Papan Peringkat Referral — jasasebar.com" },
      {
        name: "description",
        content:
          "Lihat pengguna paling aktif mengajak teman ke jasasebar.com. Peringkat dihitung dari jumlah undangan yang berhasil membuat listing.",
      },
      { property: "og:title", content: "Papan Peringkat Referral — jasasebar.com" },
      {
        property: "og:description",
        content: "Peringkat pengundang teraktif di jasasebar.com berdasarkan undangan sukses.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ReferralPage,
});

function ReferralPage() {
  const q = useQuery({
    queryKey: ["referral-leaderboard"],
    queryFn: () => getReferralLeaderboard(),
    refetchInterval: 120_000,
  });

  return (
    <div className="min-h-screen bg-background">
      <ScrollProgress />
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl px-4 py-6">
        <Reveal>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-xl font-bold">Papan peringkat referral</h1>
              <p className="mt-1 text-xs text-muted-foreground">
                Peringkat pengguna paling aktif mengajak teman. Undangan dihitung sukses saat teman Anda membuat
                listing pertamanya.
              </p>
            </div>
            <Button asChild size="sm">
              <Link to="/akun">
                <Gift className="size-4" /> Ambil kode saya
              </Link>
            </Button>
          </div>
        </Reveal>

        {q.isPending && (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        )}

        {q.data && q.data.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              Belum ada undangan tercatat. Jadilah yang pertama mengisi papan ini.
            </CardContent>
          </Card>
        )}

        <ul className="space-y-2">
          {q.data?.map((row, i) => (
            <li key={row.code}>
              <Reveal delay={i * 0.02}>
                <Card className={i === 0 ? "border-primary/40" : undefined}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold tabular-nums">
                      {i === 0 ? <Crown className="size-4 text-primary" /> : i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{row.name}</p>
                      <p className="text-xs text-muted-foreground">Kode {row.code}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <Trophy className="size-3" /> {row.converted} sukses
                      </Badge>
                      <span className="text-xs text-muted-foreground tabular-nums">{row.invited} undangan</span>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            </li>
          ))}
        </ul>
      </main>
      <SiteFooter />
    </div>
  );
}
